import React, { Children } from 'react';
import { motion } from 'framer-motion';
import { Search, Smartphone, Eye, ShieldCheck, ArrowRightLeft, Zap, Target } from 'lucide-react';
const fadeUp = {
  hidden: {
    opacity: 0,
    y: 20
  },
  visible: {
    opacity: 1,
    y: 0,
    transition: {
      duration: 0.6,
      ease: 'easeOut'
    }
  }
};
const stagger = {
  visible: {
    transition: {
      staggerChildren: 0.1
    }
  }
};
// PAGE 10: ACME Telco: discovery
export function Page10() {
  const objectives = [{
    title: 'Deflect Tier 1 Volume',
    desc: 'Automate high-frequency transactional queries (billing, plan changes).',
    icon: <TrendingUp className="w-6 h-6 text-green-600" data-id="element-501" />
  }, {
    icon: <Zap className="w-6 h-6 text-amber-500" data-id="element-502" />,
    title: 'Faster Resolution',
    desc: 'Accelerate outcomes across both on-site and virtual support journeys.'
  }, {
    icon: <Users className="w-6 h-6 text-emerald-500" data-id="element-503" />,
    title: 'Inclusive Experience',
    desc: 'Consistent, high-quality support across diverse demographics and regions.'
  }];
  return <motion.div initial="hidden" animate="visible" variants={stagger} className="max-w-5xl mx-auto w-full" data-id="element-504">
      <motion.div variants={fadeUp} className="text-center mb-12" data-id="element-505">
        <div className="inline-flex items-center gap-2 px-3 py-1 rounded-full bg-green-50 text-sierra-blue text-sm font-medium mb-4" data-id="element-506">
          <Search className="w-4 h-4" data-id="element-507" /> Discovery Findings
        </div>
        <h2 className="text-4xl font-bold text-sierra-dark" data-id="element-508">
          ACME Telco Objectives
        </h2>
      </motion.div>

      <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mb-12" data-id="element-509">
        {objectives.map((obj, i) => <motion.div key={i} variants={fadeUp} className="bg-white p-8 rounded-2xl sierra-shadow border border-gray-100" data-id="element-510">
            <div className="w-12 h-12 rounded-xl bg-gray-50 flex items-center justify-center mb-6" data-id="element-511">
              {obj.icon}
            </div>
            <h3 className="text-xl font-semibold mb-3" data-id="element-512">{obj.title}</h3>
            <p className="text-gray-600 leading-relaxed" data-id="element-513">{obj.desc}</p>
          </motion.div>)}
      </div>

      <motion.div variants={fadeUp} className="bg-gray-50 border border-gray-200 rounded-xl p-6 text-center max-w-2xl mx-auto" data-id="element-514">
        <p className="text-lg font-medium text-gray-800" data-id="element-515">
          "No big transformation — we'll ship value in phases."
        </p>
      </motion.div>
    </motion.div>;
}
// PAGE 11: Use case selection
export function Page11() {
  return <motion.div initial="hidden" animate="visible" variants={stagger} className="max-w-4xl mx-auto w-full" data-id="element-516">
      <motion.h2 variants={fadeUp} className="text-3xl font-bold mb-10 text-center" data-id="element-517">
        The Hero Journey
      </motion.h2>

      <motion.div variants={fadeUp} className="bg-gradient-to-br from-sierra-blue to-blue-800 rounded-3xl p-10 text-white shadow-xl mb-8 relative overflow-hidden" data-id="element-518">
        <div className="absolute top-0 right-0 opacity-10 transform translate-x-1/4 -translate-y-1/4" data-id="element-519">
          <Smartphone className="w-64 h-64" data-id="element-520" />
        </div>
        <div className="relative z-10" data-id="element-521">
          <h3 className="text-green-200 font-semibold tracking-wider uppercase text-sm mb-2" data-id="element-522">
            Selected Use Case
          </h3>
          <h4 className="text-4xl font-bold mb-4" data-id="element-523">
            Device Setup + eSIM Activation
          </h4>
        </div>
      </motion.div>

      <div className="grid grid-cols-1 md:grid-cols-3 gap-6" data-id="element-524">
        <motion.div variants={fadeUp} className="bg-white p-6 rounded-2xl sierra-shadow border border-gray-100" data-id="element-525">
          <h5 className="font-semibold text-lg mb-2" data-id="element-526">High Impact</h5>
          <p className="text-gray-600 text-sm" data-id="element-527">
            High-volume, high-friction driver of contact centre cost.
          </p>
        </motion.div>
        <motion.div variants={fadeUp} className="bg-white p-6 rounded-2xl sierra-shadow border border-gray-100" data-id="element-528">
          <h5 className="font-semibold text-lg mb-2" data-id="element-529">Measurable</h5>
          <p className="text-gray-600 text-sm" data-id="element-530">
            Clear outcomes: containment, time-to-resolution, fewer repeat
            contacts.
          </p>
        </motion.div>
        <motion.div variants={fadeUp} className="bg-white p-6 rounded-2xl sierra-shadow border border-gray-100" data-id="element-531">
          <h5 className="font-semibold text-lg mb-2" data-id="element-532">Low Risk</h5>
          <p className="text-gray-600 text-sm" data-id="element-533">
            Minimal transformation: integrates with existing systems; roll out
            incrementally.
          </p>
        </motion.div>
      </div>
    </motion.div>;
}
// PAGE 12: Demo setup
export function Page12() {
  const lookFor = [{
    icon: <Zap className="w-5 h-5 text-amber-500" data-id="element-534" />,
    text: 'Notice when the agent takes action vs when it just answers.'
  }, {
    icon: <ShieldCheck className="w-5 h-5 text-emerald-500" data-id="element-535" />,
    text: 'Guardrails: verification, confirmation gates, PII masking.'
  }, {
    icon: <ArrowRightLeft className="w-5 h-5 text-blue-500" data-id="element-536" />,
    text: 'Escalation: when it hands off and what context it sends.'
  }, {
    icon: <Target className="w-5 h-5 text-purple-500" data-id="element-537" />,
    text: 'Outcome focus: speed, clarity, next-best action.'
  }];
  return <motion.div initial="hidden" animate="visible" variants={stagger} className="max-w-4xl mx-auto w-full" data-id="element-538">
      <motion.div variants={fadeUp} className="text-center mb-12" data-id="element-539">
        <Eye className="w-12 h-12 text-sierra-blue mx-auto mb-6" data-id="element-540" />
        <h2 className="text-4xl font-bold text-sierra-dark mb-4" data-id="element-541">
          How to watch this demo
        </h2>
        <p className="text-xl text-gray-500" data-id="element-542">
          Look for orchestration, not just conversation.
        </p>
      </motion.div>

      <motion.div variants={fadeUp} className="bg-white rounded-3xl sierra-shadow border border-gray-100 p-8 md:p-12" data-id="element-543">
        <div className="grid grid-cols-1 md:grid-cols-2 gap-8" data-id="element-544">
          {lookFor.map((item, i) => <div key={i} className="flex items-start gap-4" data-id="element-545">
              <div className="p-3 bg-gray-50 rounded-xl shrink-0" data-id="element-546">
                {item.icon}
              </div>
              <p className="text-lg text-gray-700 font-medium pt-2 leading-snug" data-id="element-547">
                {item.text}
              </p>
            </div>)}
        </div>
      </motion.div>
    </motion.div>;
}
// Helper imports for Page 10
import { TrendingUp, Users } from 'lucide-react';